import type { TransferStatusEnum } from '~/utils/receipt-model/ReceiptModel'

export enum InvoiceType {
  SINGLE_INVOICE = 'single_invoice',
  SEPARATE_INVOICES = 'separate_invoices',
  SERVICE_INVOICE = 'service_invoice',
}

export enum IssuanceOption {
  ZERO_HUNDRED = '0_100',
  FIFTY_FIFTY = '50_50',
  NINETY_TEN = '90_10',
  SIXTY_FORTY = '60_40',
  HUNDRED_ZERO = '100_0',
}

export enum InvoiceKind {
  COMMISSION = 'commission',
  PRODUCTS = 'products',
  SERVICE_PRODUCT = 'service_product',
  CORRECTION_LETTER = 'correction_letter',
}

export enum PaymentModelStatus {
  IN_PROGRESS = 'in_progress',
  UNDER_ANALYSIS = 'under_analysis',
  TO_REVIEW = 'to_review',
  APPROVED = 'approved',
  REPROVED = 'reproved',
  AWAITING_INVOICE_EQUIPMENT = 'awaiting_invoice_equipment',
}

export enum InvoiceStatus {
  APPROVED = 'approved',
  DENIED = 'denied',
  AWAITING_ANALYSIS = 'awaiting_analysis',
}

export type PayoutOptionTone = 'a' | 'b' | 'c' | 'neutral'

export const PAYOUT_OPTION_DISPLAY: Record<string, { letter: string, tone: PayoutOptionTone }> = {
  [IssuanceOption.NINETY_TEN]: { letter: 'A', tone: 'a' },
  [IssuanceOption.FIFTY_FIFTY]: { letter: 'B', tone: 'b' },
  [IssuanceOption.ZERO_HUNDRED]: { letter: 'C', tone: 'c' },
}

export const PAYOUT_OPTION_SERVICE_LABEL_KEYS = {
  split: 'receipt-model.model_select.service.split',
  afterInstallation: 'receipt-model.model_select.service.after_installation',
  inCash: 'receipt-model.model_select.service.in_cash',
  unknown: 'receipt-model.model_select.service.unknown',
} as const

export type PayoutOptionServiceLabelKey = typeof PAYOUT_OPTION_SERVICE_LABEL_KEYS[keyof typeof PAYOUT_OPTION_SERVICE_LABEL_KEYS]

export interface PayoutOptionDisplay {
  code: string
  letter: string
  tone: PayoutOptionTone
  modelLabel: string
  serviceLabelKey: PayoutOptionServiceLabelKey
  serviceLabelParams: Record<string, string | number>
}

// Code format "{% upfront}_{% after installation}", adding up to 100 (e.g. '90_10').
const PAYOUT_OPTION_CODE_PATTERN = /^\d+_\d+$/

function hasOwnKey<T extends object>(map: T, key: string): key is Extract<keyof T, string> {
  return Object.prototype.hasOwnProperty.call(map, key)
}

function getPayoutOptionServiceLabel(code: string): Pick<PayoutOptionDisplay, 'serviceLabelKey' | 'serviceLabelParams'> {
  const unknownLabel = { serviceLabelKey: PAYOUT_OPTION_SERVICE_LABEL_KEYS.unknown, serviceLabelParams: { code } }

  if (!PAYOUT_OPTION_CODE_PATTERN.test(code))
    return unknownLabel

  const [cash, installation] = code.split('_').map(Number)

  if (cash + installation !== 100)
    return unknownLabel

  if (cash === 0)
    return { serviceLabelKey: PAYOUT_OPTION_SERVICE_LABEL_KEYS.afterInstallation, serviceLabelParams: {} }

  if (installation === 0)
    return { serviceLabelKey: PAYOUT_OPTION_SERVICE_LABEL_KEYS.inCash, serviceLabelParams: {} }

  return { serviceLabelKey: PAYOUT_OPTION_SERVICE_LABEL_KEYS.split, serviceLabelParams: { cash, installation } }
}

export function getPayoutOptionDisplay(code: string): PayoutOptionDisplay {
  const display = hasOwnKey(PAYOUT_OPTION_DISPLAY, code)
    ? PAYOUT_OPTION_DISPLAY[code]
    : { letter: '', tone: 'neutral' as const }

  return {
    code,
    letter: display.letter,
    tone: display.tone,
    modelLabel: code.replace(/_/g, '-'),
    ...getPayoutOptionServiceLabel(code),
  }
}

export type TransferVisualStatus = 'analysis' | 'pending' | 'scheduled' | 'paid' | 'error' | 'cancelled'

export const TRANSFER_STATUS_VISUAL: Record<TransferStatusEnum, TransferVisualStatus> = {
  waiting_send_payment: 'scheduled',
  awaiting_payment: 'pending',
  payment_blocked: 'pending',
  paid: 'paid',
  fail: 'error',
  denied: 'error',
  error: 'error',
  canceled: 'cancelled',
}

// "In analysis" is not a transfer status: it depends on the step status (see `useReceivableValues`).
export function getTransferVisualStatus(status: string | null | undefined): TransferVisualStatus {
  if (!status || !hasOwnKey(TRANSFER_STATUS_VISUAL, status))
    return 'pending'

  return TRANSFER_STATUS_VISUAL[status]
}
